import { useI18n } from "../lib/i18n";
import { Play } from "lucide-react";

const IMG = "https://static.prod-images.emergentagent.com/jobs/d71174fa-7b21-4a1b-abe7-f0b7113894a9/images";

// A deliberately VARIED showcase — every piece is a different subject, model and format.
const SHOWCASE = [
  {
    kind: "image",
    src: `${IMG}/6bb1e060335431d2b584af73f78fa375cee0485b310aa6d198705c3283dba84c.jpeg`,
    model: "Midjourney", provider: "Midjourney", tag: "Portrait",
    prompt: "Cinematic portrait, woman on a neon-lit Tokyo rooftop, translucent raincoat, teal & orange rim light, 85mm, film grain --ar 2:3",
  },
  {
    kind: "video",
    src: "/gallery/show-lava.mp4", poster: "/gallery/show-lava.jpg",
    model: "Veo 3.1", provider: "Google", tag: "Video",
    prompt: "Aerial drone flying low over glowing molten lava rivers at night, rising embers, volumetric smoke, cinematic, 5s.",
  },
  {
    kind: "image",
    src: `${IMG}/a925d2c585b6ef413264bc66290cf9a783dd38d8e2e8f9b12e6a28bb011b2db7.jpeg`,
    model: "GPT Image 2", provider: "OpenAI", tag: "Product",
    prompt: "Luxury perfume bottle suspended in an amber liquid splash with golden silk, dramatic studio light, macro advertising still.",
  },
  {
    kind: "image",
    src: `${IMG}/8658782602407081c969a33e8ef2a33f4206b5146d0a2800eff0442e6a2384b4.jpeg`,
    model: "FLUX.2", provider: "Black Forest Labs", tag: "Concept art",
    prompt: "Floating rocky islands with glowing orange waterfalls into clouds at golden hour, lone traveler, volumetric rays --ar 3:2",
  },
  {
    kind: "image",
    src: `${IMG}/48c063bcc9ba7e65d800ef999694bd08d65f02a3f355e08c4968067052802f25.jpeg`,
    model: "Nano Banana Pro", provider: "Google", tag: "3D character",
    prompt: "Glossy 3D render, adorable round robot with big expressive eyes, warm orange accent lights, Pixar-style, clean studio.",
  },
  {
    kind: "video",
    src: "/gallery/show-ink.mp4", poster: "/gallery/show-ink.jpg",
    model: "Kling 3 Pro", provider: "Kuaishou", tag: "Video",
    prompt: "Slow-motion macro of vibrant ink clouds blooming in clear water, backlit, orange, teal and magenta, abstract, 5s.",
  },
  {
    kind: "image",
    src: `${IMG}/5878ea0097752863082f419c7fa6ed28f47a9400d266cd2014bbb96ca62ddb93.jpeg`,
    model: "Ideogram", provider: "Ideogram", tag: "Wildlife",
    prompt: "Close-up of a majestic tiger emerging from jungle shadows, dramatic rim light on orange fur, intense eyes, ultra sharp.",
  },
  {
    kind: "image",
    src: `${IMG}/2842abb23bb025dd1142146391c662d194dd814c2386a3d29e7387a4f5f129cc.jpeg`,
    model: "Recraft", provider: "Recraft", tag: "Interior",
    prompt: "Minimalist Scandinavian living room at golden hour, cream sofa, wooden textures, indoor plants, cozy editorial photo.",
  },
  {
    kind: "image",
    src: `${IMG}/8c747e4dde421aca2fcd4a9a38593680f0402f91692a4ea557a034e41c387769.jpeg`,
    model: "Seedream", provider: "ByteDance", tag: "Automotive",
    prompt: "Vintage red convertible on a winding Amalfi coastal road at sunset, warm orange light, motion blur, cinematic automotive photo.",
  },
];

export default function ExampleGallery() {
  const { t } = useI18n();

  return (
    <section id="examples" className="py-24 px-6 bg-[#F4EFEB] text-[#14141A]">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-bold mb-3">{t("gallery_label")}</div>
          <h2 className="font-black text-4xl md:text-5xl tracking-tight mb-3 text-[#14141A]">{t("gallery_title")}</h2>
          <p className="text-[#524E48]">{t("gallery_sub")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SHOWCASE.map((ex, i) => (
            <div
              key={i}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#D6CFC4] bg-[#14141A] shadow-sm hover:shadow-[0_12px_40px_rgba(249,115,22,0.18)] transition-shadow duration-300"
              data-testid={`gallery-card-${i}`}
            >
              {ex.kind === "video" ? (
                <div className="relative h-full">
                  <video
                    src={ex.src}
                    poster={ex.poster}
                    autoPlay muted loop playsInline preload="metadata"
                    className="w-full h-full object-cover block"
                    data-testid={`gallery-video-${i}`}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-[#14141A]/80 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-[#F4EFEB] tracking-wide">
                    <Play className="w-3 h-3 text-orange-400 fill-orange-400" /> {ex.tag}
                  </span>
                </div>
              ) : (
                <div className="relative h-full">
                  <img
                    src={ex.src}
                    alt={`${ex.model} — ${ex.tag}`}
                    loading="lazy"
                    className="w-full h-full object-cover block transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <span className="absolute top-3 left-3 rounded-full bg-[#14141A]/80 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-[#F4EFEB] tracking-wide">
                    {ex.tag}
                  </span>
                </div>
              )}

              {/* Model + prompt reveal */}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[#0E0E12] via-[#0E0E12]/80 to-transparent">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-black text-sm tracking-tight text-[#F4EFEB]">{ex.model}</span>
                  <span className="text-[9px] uppercase tracking-widest text-orange-400 font-bold">{ex.provider}</span>
                </div>
                <p className="text-[11px] leading-snug font-mono text-zinc-300 max-h-0 opacity-0 group-hover:max-h-32 group-hover:opacity-100 group-hover:mt-1.5 transition-all duration-300 overflow-hidden">
                  {ex.prompt}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
